import type { User } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { useSidebarCollapsed } from '@web/data/repositories';
import { AppIcons, Avatar, AvatarFallback, Button } from '@web/ui/core/atoms';
import { useAuthz } from '@web/ui/core/hooks';
import { PopoverMenu } from '@web/ui/core/molecules';
import { fireAndForget, twcn } from '@web/ui/core/utils';

import { OrgSwitcher } from './org_switcher';
import { StatusChip } from './status_chip';
import { type PresenceStatus, useProfileMenu } from './use_profile_menu';

const STATUS_ORDER: PresenceStatus[] = ['online', 'busy', 'away', 'offline'];

const STATUS_DOT: Record<PresenceStatus, string> = {
  online: 'bg-tone-green-fg',
  busy: 'bg-tone-amber-fg',
  away: 'bg-faint-foreground',
  offline: 'bg-transparent shadow-inset-faint',
};

type StatusAvatarSize = 'md' | 'lg';

function AvatarWithStatus({
  displayName,
  size,
  status,
}: Readonly<{
  displayName: string;
  size: StatusAvatarSize;
  status: PresenceStatus;
}>): React.JSX.Element {
  const dotSize = size === 'lg' ? 'h-2.75 w-2.75' : 'h-2.25 w-2.25';
  return (
    <span className="relative inline-flex">
      <Avatar size={size} tone="brand">
        <AvatarFallback displayName={displayName} />
      </Avatar>
      <span
        aria-hidden
        className={twcn(
          'absolute -bottom-px -right-px inline-block rounded-pill ring-2 ring-paper',
          dotSize,
          STATUS_DOT[status],
        )}
      />
    </span>
  );
}

type ProfileMenuProps = {
  user: User | null;
  onSignOut: () => void | Promise<void>;
};

/**
 * Rail-foot profile popup. Used by the org workspace and settings
 * shells so the agent always has the same control surface. Slots:
 * trigger row (avatar + name + org/status), then on open: identity
 * head (link to /settings/profile), status chip row, org switcher,
 * sign-out.
 */
export function ProfileMenu({ user, onSignOut }: Readonly<ProfileMenuProps>): React.JSX.Element {
  const ctrl = useProfileMenu();
  const authz = useAuthz();
  const collapsed = useSidebarCollapsed((s) => s.collapsed);
  const canManageOrg = authz.has('accounts:members:read');

  const displayName =
    user?.name && user.name.trim().length > 0 ? user.name : t('home.profileMenu.accountFallback');
  const displayEmail = user?.email ?? '';
  const otherOrgs = ctrl.orgs.filter((o) => o.id !== ctrl.activeOrg?.id);
  const statusLabel = t(`profileMenu.status.${ctrl.status}`);

  return (
    <PopoverMenu
      side="top"
      align="start"
      sideOffset={8}
      width="lg"
      padded={false}
      renderTrigger={({ onClick, open }) =>
        collapsed ? (
          <button
            type="button"
            onClick={onClick}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={displayName}
            className="inline-flex items-center justify-center rounded-md p-0.5 transition-colors duration-fast ease-standard hover:bg-paper"
          >
            <AvatarWithStatus displayName={displayName} size="md" status={ctrl.status} />
          </button>
        ) : (
          <button
            type="button"
            onClick={onClick}
            aria-haspopup="menu"
            aria-expanded={open}
            className="flex w-full items-center gap-2.5 rounded-md border border-border-soft bg-card px-2 py-1.5 text-left transition-colors duration-fast ease-standard hover:border-border hover:bg-paper"
          >
            <AvatarWithStatus displayName={displayName} size="md" status={ctrl.status} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-meta font-medium text-foreground">{displayName}</span>
              <span className="truncate font-mono text-mono-xs tracking-eyebrow-narrow text-faint-foreground">
                {ctrl.activeOrg?.name ?? ''} · {statusLabel}
              </span>
            </span>
            <AppIcons name={open ? 'caret-down' : 'caret-up'} size={12} />
          </button>
        )
      }
    >
      {({ close }) => (
        <div className="font-sans">
          <button
            type="button"
            onClick={() => {
              close();
              ctrl.goToProfile();
            }}
            className="flex w-full items-center gap-2.5 rounded-t-lg border-0 border-b border-border-soft bg-paper px-3.5 py-3.5 text-left transition-colors duration-fast ease-standard hover:bg-muted"
          >
            <AvatarWithStatus displayName={displayName} size="lg" status={ctrl.status} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-card-title font-medium text-foreground">
                {displayName}
              </span>
              <span className="mt-0.5 block truncate font-mono text-card-id text-muted-foreground">
                {displayEmail}
              </span>
            </span>
            <AppIcons name="arrow-right" size={13} className="text-faint-foreground" />
          </button>

          <div className="px-3 py-2.5">
            <div className="mb-2 px-0.5 font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-muted-foreground">
              {t('profileMenu.section.status')}
            </div>
            <div className="flex flex-wrap gap-1">
              {STATUS_ORDER.map((s) => (
                <StatusChip
                  key={s}
                  status={s}
                  label={t(`profileMenu.status.${s}`)}
                  isActive={ctrl.status === s}
                  onSelect={ctrl.setStatus}
                />
              ))}
            </div>
          </div>

          {ctrl.activeOrg ? (
            <div className="border-t border-border-soft px-3 py-2.5">
              <div className="mb-2 px-0.5 font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-muted-foreground">
                {t('profileMenu.section.org')}
              </div>
              <OrgSwitcher
                active={ctrl.activeOrg}
                others={otherOrgs}
                onSwitch={(slug) => {
                  close();
                  ctrl.switchOrg(slug);
                }}
              />
              {canManageOrg ? (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    ctrl.goToMembers();
                  }}
                  className="mt-2 flex w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2 py-1.5 text-left text-meta text-foreground-default transition-colors hover:bg-subtle"
                >
                  <AppIcons name="building-office" size={14} />
                  <span className="flex-1">{t('profileMenu.org.manage')}</span>
                  <AppIcons name="arrow-right" size={11} />
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="border-t border-border-soft p-1.5">
            <Button
              variant="danger"
              onClick={() => {
                close();
                fireAndForget(
                  (async () => {
                    try {
                      await onSignOut();
                    } catch (err) {
                      // Sign-out network/SDK failures fall through to the
                      // app-wide error handler; we still leave the menu
                      // closed so the trigger button does not freeze.
                      (globalThis as Partial<Pick<typeof globalThis, 'reportError'>>).reportError?.(
                        err,
                      );
                    }
                  })(),
                );
              }}
            >
              <AppIcons name="sign-out" size={15} />
              <span className="flex-1 text-left">{t('home.profileMenu.signOut')}</span>
            </Button>
          </div>
        </div>
      )}
    </PopoverMenu>
  );
}
